import React from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { CAMPAIGN_COPY } from '../../constants/campaignCopy';
import { buildWorkspaceRoute } from '../../utils/businessFunctionRoutes';

const ICON_COMPONENTS = {
  Groups: GroupsIcon,
  FilterAlt: FilterAltIcon,
  PersonOutline: PersonOutlineIcon,
  Assessment: AssessmentIcon,
  TaskAlt: TaskAltIcon,
  Description: DescriptionIcon,
  FolderOpen: FolderOpenIcon,
};

export function isWorkspaceActive(workspace) {
  return !workspace?.isDisabled;
}

/**
 * Individual workspace card — displays icon, name, description,
 * and meta stats (tables, views, automations).
 */
export default function WorkspaceCard({
  accountId,
  accountName,
  workspace,
  canToggle = false,
  isToggling = false,
  onToggle,
  onMappingClick,
  canManageMappings = false,
  styles,
}) {
  const navigate = useNavigate();
  const { accountId: routeAccountId } = useParams();

  const IconComponent = ICON_COMPONENTS[workspace.iconName] || FolderOpenIcon;
  const iconAccent = styles.iconAccent?.[workspace.accentKey] ?? {};
  const isActive = isWorkspaceActive(workspace);

  const handleCardClick = () => {
    if (!isActive && !canToggle) return;

    const targetAccountId = accountId || routeAccountId;
    if (!targetAccountId || !workspace?.id) return;
    navigate(buildWorkspaceRoute(targetAccountId, workspace.id, { accountName }));
  };

  const handleMappingClick = (event) => {
    event.stopPropagation();
    onMappingClick?.(workspace);
  };

  return (
    <Box
      sx={{
        ...styles.workspaceCard,
        ...(!isActive ? styles.workspaceCardInactive : null),
        ...(!isActive && !canToggle ? { cursor: 'not-allowed' } : null),
      }}
      onClick={handleCardClick}
    >
      <Box sx={styles.workspaceCardTop}>
        <Box sx={styles.workspaceCardInfo}>
          <Box sx={{ ...styles.workspaceIconWrap, ...iconAccent }}>
            <IconComponent sx={{ fontSize: 24, color: 'inherit' }} />
          </Box>
          <Box sx={styles.workspaceCardTextBlock}>
            <Typography sx={styles.workspaceCardName}>
              {workspace.name}
            </Typography>
            <Typography sx={styles.workspaceCardDescription}>
              {workspace.description}
            </Typography>
            {!isActive ? (
              <Typography variant="caption" color="text.secondary">
                {CAMPAIGN_COPY.list.inactive}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {canToggle ? (
          <Box
            sx={styles.workspaceCardToggle}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <FormControlLabel
              sx={{ mr: 0 }}
              onClick={(event) => event.stopPropagation()}
              control={
                <Switch
                  size="small"
                  checked={isActive}
                  disabled={isToggling}
                  onChange={(event) => {
                    event.stopPropagation();
                    onToggle?.(workspace, !event.target.checked);
                  }}
                  inputProps={{
                    'aria-label': isActive
                      ? CAMPAIGN_COPY.list.active
                      : CAMPAIGN_COPY.list.inactive,
                  }}
                />
              }
              label={isActive ? CAMPAIGN_COPY.list.active : CAMPAIGN_COPY.list.inactive}
            />
          </Box>
        ) : null}
      </Box>

      <Box sx={styles.workspaceCardMeta}>
        <Box sx={styles.workspaceMetaItem}>
          <TableChartOutlinedIcon sx={styles.workspaceMetaIcon} />
          {workspace.tablesCount} Tables
        </Box>
        <Box sx={styles.workspaceMetaItem}>
          <VisibilityOutlinedIcon sx={styles.workspaceMetaIcon} />
          {workspace.viewsCount} Views
        </Box>
        <Box sx={styles.workspaceMetaItem}>
          <SettingsSuggestOutlinedIcon sx={styles.workspaceMetaIcon} />
          {workspace.automationsCount} Automations
        </Box>
        {canManageMappings ? (
          <Button
            size="small"
            variant="text"
            startIcon={<AccountTreeOutlinedIcon />}
            onClick={handleMappingClick}
            sx={{ ml: 'auto', minWidth: 'auto' }}
          >
            Mapping
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
